import {
  controlTypes,
  controlableTypes,
  createOptionsFromEnum,
} from '../BacnetSchedule/constants';
import {
  UI_TYPE_BINARY,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_VALUE,
} from '../../../Modbus/types';
import {
  getModbusRegisterKey,
  getModbusObjectKey,
  objectKeyToRegisterKey,
} from '../../../Modbus/ModbusUnitControl';
import { engUnitLabel } from '../../../Modbus/RegisterTable';

export {
  controlTypes,
  controlableTypes,
  createOptionsFromEnum,
  getModbusRegisterKey,
  objectKeyToRegisterKey,
};

// Mirrors ModbusControlPanel's `!entry.uiTemplate && isPowerMeter` branch.
// The power-meter fallback applies only without a uiTemplate; with one,
// its authored points determine what's controllable.
export const isModbusPowerMeterFallback = (modbusUnit) => {
  if (modbusUnit?.uiTemplate) {
    return false;
  }
  const entityTemplate =
    modbusUnit?.entityTemplate ?? modbusUnit?.paramTemplateData?.entityTemplate;
  const entityType = modbusUnit?.entityType ?? modbusUnit?.paramTemplateData?.entityType;
  return entityTemplate === true && entityType === 'powerMeter';
};

const DEFAULT_BINARY_ENUM = { 1: 'On', 0: 'Off' };
const DEFAULT_REVERSE_BINARY_ENUM = { 0: 'On', 1: 'Off' };

// uiTemplate point's authored controlType, which can become stale if the
// register's uiType changes. Mirrors ModbusUIRenderer's disabled checks so
// non-interactive points aren't offered for scheduling based on row uiType.
const isRowEditableForPanelControlType = (row, panelControlType) => {
  if (!row || row.readOnly) {
    return false;
  }
  switch (panelControlType) {
    case 'onoff':
    case 'toggle':
      return row.uiType === UI_TYPE_BINARY || row.uiType === UI_TYPE_REVERSE_BINARY;
    case 'dropdown':
      return row.uiType === UI_TYPE_ENUMERATION && !!Object.keys(row.customEnum || {}).length;
    case 'updown':
      return row.uiType === UI_TYPE_VALUE;
    default:
      return false;
  }
};

// ControlType for a plain row (no uiTemplate point), matching
// ModbusUnitControl's row-only switch and always derived from uiType.
const inferPanelControlTypeFromUiType = (uiType) => {
  switch (uiType) {
    case UI_TYPE_BINARY:
    case UI_TYPE_REVERSE_BINARY:
      return 'toggle';
    case UI_TYPE_ENUMERATION:
      return 'dropdown';
    case UI_TYPE_VALUE:
      return 'updown';
    default:
      return 'text';
  }
};

const mapPanelControlTypeToScheduleType = (panelControlType) => {
  switch (panelControlType) {
    case 'onoff':
    case 'toggle':
      return controlTypes.toggle;
    case 'dropdown':
      return controlTypes.list;
    case 'updown':
      return controlTypes.numericValue;
    default:
      return controlTypes.informationText;
  }
};

const buildBaseConfig = (row, name) => ({
  name,
  readOnly: row.readOnly,
  units: row.units,
  symbol: engUnitLabel(row.units),
});

const getCustomEnumForRow = (row) => {
  if (row.uiType === UI_TYPE_ENUMERATION) {
    return row.customEnum || {};
  }
  if (row.uiType === UI_TYPE_BINARY) {
    return row.customEnum || DEFAULT_BINARY_ENUM;
  }
  if (row.uiType === UI_TYPE_REVERSE_BINARY) {
    return row.customEnum || DEFAULT_REVERSE_BINARY_ENUM;
  }
  return undefined;
};

// Builds the schedule data-point map by register key, mirroring BACnet's
// mergeUiTemplatePointsWithParamTemplate. With a uiTemplate, only authored
// points that pass the same editability check as the live panel are included;
// without one, falls back to the row's uiType.
export const mergeModbusRowsToDataPoints = (rows, uiTemplatePoints) => {
  const points = {};
  const rowList = rows || [];

  const getComputedEntries = () => {
    if (uiTemplatePoints && uiTemplatePoints.length) {
      return uiTemplatePoints
        .map((point) => {
          const row = rowList[point.rowIndex];
          if (!row) {
            return null;
          }
          return { row, label: point.labelName || row.name, panelControlType: point.controlType };
        })
        .filter(Boolean);
    }
    return rowList.map((row) => ({
      row,
      label: row.name,
      panelControlType: inferPanelControlTypeFromUiType(row.uiType),
    }));
  };
  const entries = getComputedEntries();

  entries.forEach(({ row, label, panelControlType }) => {
    if (!isRowEditableForPanelControlType(row, panelControlType)) {
      return;
    }
    const key = getModbusRegisterKey(row);
    if (!key) {
      return;
    }
    const controlType = mapPanelControlTypeToScheduleType(panelControlType);
    if (controlType === controlTypes.informationText) {
      return;
    }

    points[key] = {
      objectKey: getModbusObjectKey(row),
      label,
      controlType,
      config: {
        ...buildBaseConfig(row, label),
        customEnum:
          controlType === controlTypes.numericValue
            ? { min: row.minValue ?? 0, max: row.maxValue ?? 100, step: row.step ?? 1 }
            : getCustomEnumForRow(row),
      },
    };
  });

  return points;
};

// All rows keyed by register key, including read-only/non-controllable ones
// and regardless of uiTemplate. Unlike mergeModbusRowsToDataPoints, this
// resolves names for saved points that are no longer controllable.
export const getModbusRowsByKey = (rows) => {
  const points = {};

  (rows || []).forEach((row) => {
    const key = getModbusRegisterKey(row);
    if (!key) {
      return;
    }
    points[key] = {
      label: row.name,
      config: buildBaseConfig(row, row.name),
    };
  });

  return points;
};
